import { loadAllOpportunityStage } from "@/redux/rtk/features/CRM/opportunityStage/opportunityStageSlice";
import BigDrawer from "@/components/Drawer/BigDrawer";
import { DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import AddOpportunityStage from "../Setup/OpportunitySetup/OpportunityStage/AddOpportunityStage";
import axios from "axios";

const OpportunityInformation = ({ form, opportunity }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { list: opportunityStageList, loading: opportunityStageLoading } =
    useSelector((state) => state.opportunityStage);
  
  // State for new fields
  const [scholarships, setScholarships] = React.useState([]);
  const [nationalities, setNationalities] = React.useState([]);
  const [universities, setUniversities] = React.useState([]);
  const [majors, setMajors] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const watchedUniversityIds = Form.useWatch("universityIds", form) || [];

  useEffect(() => {
    dispatch(loadAllOpportunityStage());
    
    // Load scholarship, nationality, university, major lists
    const loadLists = async () => {
      setLoading(true);
      try {
        // These endpoints need to be created in the backend
        const [scholarshipRes, nationalityRes, universityRes, majorRes] = await Promise.all([
          axios.get('scholarship?query=all').catch(() => ({ data: { data: [] } })),
          axios.get('nationality?query=all').catch(() => ({ data: { data: [] } })),
          axios.get('university?query=all').catch(() => ({ data: { data: [] } })),
          axios.get('major?query=all').catch(() => ({ data: { data: [] } })),
        ]);
        
        setScholarships(scholarshipRes.data?.data || []);
        setNationalities(nationalityRes.data?.data || []);
        setUniversities(universityRes.data?.data || []);
        setMajors(majorRes.data?.data || []);
      } catch (error) {
        console.error("Error loading lists:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLists();
  }, [dispatch]);

  useEffect(() => {
    if (opportunity) {
      form.setFieldsValue({
        ...opportunity,
        opportunityCloseDate: opportunity.opportunityCloseDate
          ? dayjs(opportunity.opportunityCloseDate).format("YYYY-MM-DD")
          : null,
        universityIds: opportunity.universities?.map(u => u.id) || [],
      });
    }
  }, [form, opportunity]);
  
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Form.Item
          label="Deal Name *"
          name="opportunityName"
          rules={[
            { required: true, message: "Deal Name is Required." },
          ]}>
          <Input placeholder="Deal Name" />
        </Form.Item>

        <Form.Item 
          label="Closing Date *" 
          name="opportunityCloseDate"
          rules={[
            { required: true, message: "Closing Date is Required." },
          ]}>
          <DatePicker
            placeholder="Select Closing Date"
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item
          style={{ flex: "1" }}
          label={
            <>
              Stage List *
              <BigDrawer title="Opportunity Stage">
                <AddOpportunityStage drawer={true} />
              </BigDrawer>
            </>
          }
          name="opportunityStageId"
          rules={[
            { required: true, message: "Stage is Required." },
          ]}>
          <Select
            style={{ width: "100%" }}
            loading={opportunityStageLoading}
            allowClear
            showSearch
            placeholder="Select Stage">
            {opportunityStageList?.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.opportunityStageName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          label="Passport No *" 
          name="passportNo"
          rules={[
            { required: true, message: "Passport No is Required." },
          ]}>
          <Input placeholder="Passport Number" />
        </Form.Item>

        <Form.Item 
          label="Scholarship List" 
          name="scholarshipId">
          <Select
            style={{ width: "100%" }}
            loading={loading}
            allowClear
            showSearch
            placeholder="Select Scholarship">
            {scholarships?.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.scholarshipName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          label="Nationality List *" 
          name="nationalityId"
          rules={[
            { required: true, message: "Nationality is Required." },
          ]}>
          <Select
            style={{ width: "100%" }}
            loading={loading}
            allowClear
            showSearch
            placeholder="Select Nationality">
            {nationalities?.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.nationalityName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <div className="flex flex-col">
          <Form.Item 
            label="University Multi List *" 
            name="universityIds"
            rules={[
              { required: true, message: "At least one University is Required." },
            ]}
            style={{ marginBottom: watchedUniversityIds?.length > 0 ? '8px' : '24px' }}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              loading={loading}
              allowClear
              showSearch
              placeholder="Select Universities"
              tagRender={() => <span style={{ display: 'none' }} />}>
              {universities?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.universityName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {watchedUniversityIds?.length > 0 && (
            <div className="flex flex-col gap-2 mb-4">
              {watchedUniversityIds.map(uid => {
                const uni = universities?.find(u => u.id === uid);
                if (!uni) return null;
                return (
                  <div key={uid} className="flex justify-between items-center bg-blue-50/50 border border-blue-100 px-3 py-2 rounded-md">
                    <span className="text-sm break-words whitespace-normal leading-tight text-gray-700" title={uni.universityName}>
                      {uni.universityName}
                    </span>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 cursor-pointer p-1 ml-2 shrink-0 transition-colors"
                      onClick={(e) => {
                        e.preventDefault();
                        const newIds = watchedUniversityIds.filter(id => id !== uid);
                        form.setFieldsValue({ universityIds: newIds });
                      }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Form.Item 
          label="Major (Speciality) List *" 
          name="majorId"
          rules={[
            { required: true, message: "Major is Required." },
          ]}>
          <Select
            style={{ width: "100%" }}
            loading={loading}
            allowClear
            showSearch
            placeholder="Select Major">
            {majors?.map((item) => (
              <Select.Option key={item.id} value={item.id}>
                {item.majorName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
    </>
  );
};

export default OpportunityInformation;
